import React, { useRef, useState } from "react";
import { useDispatch } from "react-redux";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Grid2 from "@mui/material/Grid2";
import Typography from "@mui/material/Typography";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import FormInput from "../Common/FormInputs/MuiTextField";
import AppButton from "../Common/Button/Button";
import AppIconButton from "../Common/Button/IconButton";
import { LabelText } from "../Common/Typography/AppTypography";
import Alert from "../layout/Alert";
import { setAlert } from "../../action/alert";
import {
  uploadAsset,
  updateAsset,
  extractAssetId,
  getAssetErrorMessage,
  clearDeletedAssetMark,
} from "../../action/asset";
import { selectMenuProps } from "../../utils/selectMenuProps";
import customConfig from "../../config";
import {
  ASSET_RESOLUTION_OPTIONS,
  fileInputStyle,
  formControlSx,
} from "./assetConstants";

function AssetUploadForm({ mode = "create" }) {
  const isUpdateTab = mode === "update";
  const dispatch = useDispatch();
  const [assetId, setAssetId] = useState("");
  const [resolution, setResolution] = useState("");
  const [tag, setTag] = useState("");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savedAssetId, setSavedAssetId] = useState("");
  const [assetIdCopied, setAssetIdCopied] = useState(false);
  const fileInputRef = useRef(null);

  const resetForm = () => {
    setAssetId("");
    setResolution("");
    setTag("");
    setFile(null);
    setSavedAssetId("");
    setAssetIdCopied(false);
    setLoading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSavedAssetId("");
    setAssetIdCopied(false);

    if (isUpdateTab && !assetId?.trim()) {
      dispatch(setAlert("Asset ID Cannot be Empty", "danger"));
      return;
    }

    if (isUpdateTab && !tag?.trim()) {
      dispatch(setAlert("Tag Cannot be Empty", "danger"));
      return;
    }

    if (!resolution) {
      dispatch(setAlert("Resolution Cannot be Empty", "danger"));
      return;
    }

    if (!file) {
      dispatch(setAlert("Please select a file to upload.", "danger"));
      return;
    }

    const trimmedAssetId = assetId?.trim() || "";
    const trimmedTag = tag?.trim() || "";

    setLoading(true);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("resolution", resolution);

    if (trimmedTag) {
      formData.append("tag", trimmedTag);
    }

    if (isUpdateTab && trimmedAssetId) {
      formData.append("assetId", trimmedAssetId);
    }

    try {
      const res = isUpdateTab
        ? await updateAsset(formData)
        : await uploadAsset(formData);
      const responseAssetId = extractAssetId(res?.data, trimmedAssetId);

      if (responseAssetId) {
        clearDeletedAssetMark(responseAssetId);
      }

      dispatch(
        setAlert(
          isUpdateTab ? customConfig.UPDATE_SUCCESS : customConfig.ADD_SUCCESS,
          "success"
        )
      );

      if (isUpdateTab) {
        resetForm();
      } else {
        setResolution("");
        setTag("");
        setFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        setSavedAssetId(responseAssetId);
      }
    } catch (err) {
      dispatch(setAlert(getAssetErrorMessage(err), "danger"));
    }

    setLoading(false);
  };

  const handleCopyAssetId = async () => {
    if (!savedAssetId) {
      return;
    }

    try {
      await navigator?.clipboard?.writeText(savedAssetId);
      setAssetIdCopied(true);
      setTimeout(() => setAssetIdCopied(false), 3000);
    } catch (err) {
      dispatch(setAlert("Failed to copy Asset ID", "danger"));
    }
  };

  return (
    <Paper sx={{ p: 2, boxShadow: 2, borderRadius: 1 }}>
      <Alert />
      <form onSubmit={handleSubmit}>
        {isUpdateTab && (
          <Grid2 container spacing={2} sx={{ mb: 2, alignItems: "center" }}>
            <Grid2 size={{ xs: 12, md: 2 }}>
              <LabelText sx={{ fontWeight: 600 }}>Asset ID:</LabelText>
            </Grid2>
            <Grid2 size={{ xs: 12, md: 10 }} sx={{ minWidth: 0 }}>
              <FormInput
                type="text"
                size="small"
                fullWidth
                label="Enter Asset ID"
                placeholder="e.g. home-banner-test"
                value={assetId}
                onChange={(e) => setAssetId(e.target.value)}
                sx={formControlSx}
              />
            </Grid2>
          </Grid2>
        )}

        <Grid2 container spacing={2} sx={{ mb: 2, alignItems: "center" }}>
          <Grid2 size={{ xs: 12, md: 2 }}>
            <LabelText sx={{ fontWeight: 600 }}>Resolution:</LabelText>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 10 }} sx={{ minWidth: 0 }}>
            <FormInput
              type="select"
              size="small"
              fullWidth
              label="Select Resolution"
              value={resolution}
              onChange={(e) => setResolution(e.target.value)}
              options={ASSET_RESOLUTION_OPTIONS}
              MenuProps={selectMenuProps}
              sx={formControlSx}
            />
          </Grid2>
        </Grid2>

        <Grid2 container spacing={2} sx={{ mb: 2, alignItems: "center" }}>
          <Grid2 size={{ xs: 12, md: 2 }}>
            <LabelText sx={{ fontWeight: 600 }}>Tag:</LabelText>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 10 }} sx={{ minWidth: 0 }}>
            <FormInput
              type="text"
              size="small"
              fullWidth
              label="Enter Tag"
              placeholder="e.g. splash"
              value={tag}
              onChange={(e) => setTag(e.target.value)}
              sx={formControlSx}
            />
          </Grid2>
        </Grid2>

        <Grid2 container spacing={2} sx={{ mb: 3, alignItems: "center" }}>
          <Grid2 size={{ xs: 12, md: 2 }}>
            <LabelText sx={{ fontWeight: 600 }}>File Upload:</LabelText>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 10 }} sx={{ minWidth: 0 }}>
            <Box sx={formControlSx}>
              <input
                ref={fileInputRef}
                type="file"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                style={fileInputStyle}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
                Any file type (images, animations, JSON, video, etc.)
              </Typography>
              {file && (
                <Typography variant="body2" sx={{ mt: 1, color: "text.primary" }}>
                  <strong>Selected File:</strong> {file.name}
                  {file.type ? ` (${file.type})` : ""}
                </Typography>
              )}
            </Box>
          </Grid2>
        </Grid2>

        {!isUpdateTab && savedAssetId && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 2,
              color: "success.main",
            }}
          >
            <Typography
              variant="body2"
              sx={{ wordBreak: "break-all" }}
            >
              <strong>Asset ID:</strong> {savedAssetId}
            </Typography>
            <AppIconButton
              size="small"
              variant="ghost"
              tooltip="Copy Asset ID"
              onClick={handleCopyAssetId}
              icon={
                assetIdCopied ? (
                  <CheckCircleOutlineIcon fontSize="small" />
                ) : (
                  <FileCopyIcon fontSize="small" />
                )
              }
            />
          </Box>
        )}

        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
          <AppButton
            variant="primary"
            size="medium"
            type="submit"
            disabled={loading}
            loading={loading}
          >
            {isUpdateTab ? "Update Asset" : "Create Asset"}
          </AppButton>
          <AppButton
            variant="ghost"
            size="medium"
            type="button"
            onClick={resetForm}
            disabled={loading}
          >
            Reset
          </AppButton>
        </Box>
      </form>
    </Paper>
  );
}

export default AssetUploadForm;
